/**
 * ALTIORA · Capabilities client — mirrors backend `/api/capabilities/mine`.
 *
 * The founder-truth for the UI: what can the current user do right now,
 * given their roles + licenses + active modules + entitlements + grants.
 *
 * Two-tier strategy:
 *   1. On login (and every /me refresh), we prefetch `/capabilities/mine`
 *      into a global state — synchronous checks then read from cache.
 *   2. For dossier-scoped checks, use `useCapCheck(key, {dossierId})` which
 *      lazily calls `/capabilities/check` and caches per (key, dossier).
 *
 * The frontend NEVER re-implements the resolver — it reflects the backend.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CapsContext = createContext({
  loading: true,
  decisions: {},         // { "csf.ai_narrative": {allowed, reason_fr, missing[]} }
  entitlements: [],      // ["ai.advanced", ...]
  plan: null,            // "elia_plus"
  refresh: () => {},
});

export function CapabilitiesProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState({
    loading: true, decisions: {}, entitlements: [], plan: null,
  });

  const load = useCallback(async () => {
    if (!user) {
      setState({ loading: false, decisions: {}, entitlements: [], plan: null });
      return;
    }
    try {
      const [{ data: mine }, { data: me }] = await Promise.all([
        api.get("/capabilities/mine"),
        api.get("/auth/me"),
      ]);
      setState({
        loading: false,
        decisions: mine.decisions || {},
        entitlements: me.entitlements || [],
        plan: me.plan_id || "elia_free",
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) load();
    // Reload capabilities whenever the persona switch fires
    const on = () => load();
    window.addEventListener("elia:role:changed", on);
    return () => window.removeEventListener("elia:role:changed", on);
  }, [authLoading, load]);

  return (
    <CapsContext.Provider value={{ ...state, refresh: load }}>
      {children}
    </CapsContext.Provider>
  );
}

/** Full context accessor. */
export function useCapabilities() {
  return useContext(CapsContext);
}

/** Synchronous capability check (uses the prefetched /mine cache). */
export function useCap(key) {
  const { decisions, loading } = useCapabilities();
  if (loading) return { allowed: false, reason_fr: "", missing: [], loading: true };
  const d = decisions[key];
  if (!d) return { allowed: false, reason_fr: "Capacité inconnue", missing: ["unknown"] };
  return { ...d, loading: false };
}

/** Async capability check with dossier context — for dossier-scoped gates. */
export function useCapCheck(key, { dossierId } = {}) {
  const [result, setResult] = useState({ allowed: false, reason_fr: "", missing: [], loading: true });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = { key };
        if (dossierId) params.dossier_id = dossierId;
        const { data } = await api.get("/capabilities/check", { params });
        if (!cancelled) setResult({ ...data, loading: false });
      } catch (err) {
        if (!cancelled) setResult({ allowed: false, reason_fr: "Erreur", missing: ["error"], loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [key, dossierId]);
  return result;
}

/** Convenience: does the user hold this entitlement key ? */
export function useEntitlement(key) {
  const { entitlements } = useCapabilities();
  return entitlements.includes(key);
}

/**
 * Declarative gate component — hides the child when the capability is denied.
 * When `fallback` is provided (e.g. a PaywallTeaser), it renders that instead.
 *
 *   <Cap key="csf.ai_narrative" fallback={<PaywallTeaser reason={reason} />}>
 *     <AiNarrativePanel />
 *   </Cap>
 */
export function Cap({ capability, children, fallback = null }) {
  const { allowed, reason_fr, loading } = useCap(capability);
  if (loading) return null;
  if (!allowed) {
    if (typeof fallback === "function") return fallback({ reason: reason_fr });
    return fallback;
  }
  return children;
}
